/**
 * Voice-channel API client.
 *
 * A voice channel is its own entity — created by an admin, open to everyone in
 * the company — so this talks to /api/voice-channels and knows nothing about
 * conversations. The backend holds no call state: it lists channels, mints a
 * LiveKit join token, and reports who LiveKit currently sees in each room. The
 * media itself never touches our servers.
 *
 * `enabled: false` is a first-class answer, not an error: when the LIVEKIT_*
 * env vars are unset the feature is off and the UI hides joining entirely.
 */
import { apiFetch } from './api';
import { useAuthStore } from '../store/useAuthStore';

interface Envelope<T> {
  success: true;
  data: T;
}

function authHeaders(): Record<string, string> {
  const token = useAuthStore.getState().accessToken;
  return token ? { Authorization: `Bearer ${token}` } : {};
}

function jsonHeaders(): Record<string, string> {
  return { ...authHeaders(), 'Content-Type': 'application/json' };
}

/** A voice channel as the sidebar needs it. */
export interface VoiceChannel {
  id: string;
  name: string;
  createdAt: string;
}

/** One person LiveKit sees in a room right now. */
export interface VoiceParticipant {
  /** Their user id — the token's `identity`, so it joins to our own users. */
  userId: string;
  name: string;
  sharingScreen: boolean;
}

export interface VoicePresence {
  /** False when LiveKit isn't configured; joining stays hidden. */
  enabled: boolean;
  /** Channel id → who's inside. Missing key means nobody. */
  channels: Record<string, VoiceParticipant[]>;
}

/** Everything the client needs to connect, all of it decided server-side. */
export interface VoiceGrant {
  token: string;
  url: string;
  room: string;
}

export async function fetchVoiceChannels(): Promise<VoiceChannel[]> {
  const res = await apiFetch<Envelope<{ channels: VoiceChannel[] }>>('/api/voice-channels', {
    headers: authHeaders(),
  });
  return res.data?.channels ?? [];
}

/**
 * Presence is its own call, not a field on the channel list. The list is
 * effectively static; this changes on every join and leave and is polled at
 * 10s, so merging them would re-render the whole list on every poll.
 */
export async function fetchVoicePresence(): Promise<VoicePresence> {
  const res = await apiFetch<Envelope<VoicePresence>>('/api/voice-channels/presence', {
    headers: authHeaders(),
  });
  return { enabled: res.data?.enabled ?? false, channels: res.data?.channels ?? {} };
}

/** ADMIN only — the server rejects anyone else with 403. */
export async function createVoiceChannel(name: string): Promise<VoiceChannel> {
  const res = await apiFetch<Envelope<VoiceChannel>>('/api/voice-channels', {
    method: 'POST',
    headers: jsonHeaders(),
    body: JSON.stringify({ name }),
  });
  return res.data;
}

/** ADMIN only. The id is unchanged, so a rename never disturbs a live call. */
export async function renameVoiceChannel(id: string, name: string): Promise<VoiceChannel> {
  const res = await apiFetch<Envelope<VoiceChannel>>(
    `/api/voice-channels/${encodeURIComponent(id)}`,
    { method: 'PATCH', headers: jsonHeaders(), body: JSON.stringify({ name }) },
  );
  return res.data;
}

/** ADMIN only. The server also closes the LiveKit room, dropping whoever is in. */
export async function deleteVoiceChannel(id: string): Promise<void> {
  await apiFetch<Envelope<{ deleted: boolean }>>(`/api/voice-channels/${encodeURIComponent(id)}`, {
    method: 'DELETE',
    headers: authHeaders(),
  });
}

/**
 * Mint a join token for one channel. 403 for VIEWER (a read-only account is
 * often the external accountant) or a deactivated user, 503 when voice is off.
 */
export async function requestVoiceToken(channelId: string): Promise<VoiceGrant> {
  const res = await apiFetch<Envelope<VoiceGrant>>(
    `/api/voice-channels/${encodeURIComponent(channelId)}/token`,
    { method: 'POST', headers: authHeaders() },
  );
  return res.data;
}
